import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

// Contexts
import { AuthContext } from "../../context/AuthProvider";
import { BundleContext } from "../../context/BundleProvider";
import { ThemeContext } from "../../context/ThemeProvider";

// Components
import {
  TestReviewScore,
  TestReviewDomains,
  DeleteTestModal,
  ReadinessScoreImpact
} from "../../components/testReview";
import { TestReviewCommunity } from "../../components/common/CommonModals";

import CommonHeader from "../../components/common/CommonHeader";
import StatsGrid from "../../components/common/StatsGrid";

// API & Constants
import { GET_COMMUNITY_MULTIPLE_QUESTION_DETAILS } from "../../api/community";
import { GET_APP_DOMAIN_LIST } from "../../../app_constant";
import { STRING, STRINGS } from "../../constant";
import { 
  faCheckCircle, 
  faTimesCircle, 
  faQuestionCircle,
  faListAlt,
  faClock
} from '@fortawesome/free-solid-svg-icons';

const TestReview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { historyData, canGoBackKey, lastReadinessScore } = location.state || {};
  const testItem = historyData;
  const canGoBack = canGoBackKey;

  const { theme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  const { deleteTestHistory, profile, isVisibleCommunity } = useContext(AuthContext);

  // State management
  const [isLoadingCommunity, setLoadingCommunity] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [communityDetails, setCommunityDetails] = useState(null);
  const [communityPercentage, setCommunityPercentage] = useState(0);
  const [domainList, setDomainList] = useState([]);

  // Calculate readiness scores
  const lastReadinessScoreInt = lastReadinessScore !== undefined 
    ? Math.round(lastReadinessScore) 
    : null;
  const profileReadinessScoreInt = profile?.readiness_scores?.readiness_score !== undefined 
    ? Math.round(profile.readiness_scores.readiness_score) 
    : null;

  // Initialize data
  useEffect(() => {
    getScoreAsPerDomain();
    if (testItem?.test_questions_meta_data && isVisibleCommunity) {
      const questionIds = testItem.test_questions_meta_data.map(e => `${e.question_id}`);
      getCommunityDetails(questionIds);
    }
  }, []);

  // Get community details
  async function getCommunityDetails(ids) {
    setLoadingCommunity(true);
    try {
      const result = await GET_COMMUNITY_MULTIPLE_QUESTION_DETAILS(ids);
      setLoadingCommunity(false);

      if (result?.data?.length > 0) {
        const totalAnswered = result.data
          .map(item => item.times_answered)
          .reduce((prev, next) => prev + next);
        const totalCorrect = result.data
          .map(item => item.times_answered_correct)
          .reduce((prev, next) => prev + next);

        setCommunityDetails(result.data);
        const percentage = (totalCorrect / totalAnswered) * 100;
        setCommunityPercentage(percentage);
      } else {
        setCommunityDetails([]);
        setCommunityPercentage(0);
      }
    } catch (error) {
      setLoadingCommunity(false);
      setCommunityDetails([]);
      setCommunityPercentage(0);
    }
  }

  // Calculate domain scores
  function getScoreAsPerDomain() {
    const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
    const domainScoreList = [];

    APP_DOMAIN_LIST.forEach((domain) => {
      const totalQuestions = (testItem?.test_questions_meta_data ?? []).filter(
        e => e.topic_id === domain.id
      );
      const correctQuestions = totalQuestions.filter(
        e => e.response === STRING.correct_tag
      );
      
      const percentage = totalQuestions.length > 0
        ? ((100 * correctQuestions.length) / totalQuestions.length).toFixed(2)
        : 0;

      if (totalQuestions.length > 0) {
        domainScoreList.push({
          id: domain.id,
          title: domain.domain_name,
          value: `${percentage}%`,
          total: totalQuestions.length,
          percentage: parseFloat(percentage),
          correct: correctQuestions.length,
        });
      }
    });

    setDomainList(domainScoreList);
  }

  // Get test statistics
  function getTestStats() {
    const score = testItem?.score || 0;
    const timeTaken = testItem?.time_taken || 0;
    const totalQuestions = testItem?.total || 0;
    const avgTime = totalQuestions > 0 ? Math.floor(timeTaken / totalQuestions) : 0;

    return {
      score,
      avgTime: `${avgTime}s`,
      totalQuestions,
      correct: testItem?.correct || 0,
      incorrect: testItem?.incorrect || 0,
      unanswered: testItem?.unanswered || 0,
    };
  }

  // Get community stats
  function getCommunityStats() {
    if (!communityDetails || !isVisibleCommunity) {
      return {
        color: theme?.dashboard?.status?.incorrect || '#ef4444',
        percentage: 0,
        message: STRING.community_test_popup_desc_less_50,
        level: 'hard'
      };
    }

    if (isNaN(communityPercentage) || !isFinite(communityPercentage) || communityPercentage < 50) {
      return {
        color: theme?.dashboard?.status?.incorrect || '#ef4444',
        percentage: communityPercentage || 0,
        message: STRING.community_test_popup_desc_less_50,
        level: 'hard'
      };
    } else if (communityPercentage < 80) {
      return {
        color: theme?.dashboard?.status?.warning || '#f59e0b',
        percentage: communityPercentage,
        message: STRING.community_test_popup_desc_less_80,
        level: 'medium'
      };
    } else {
      return {
        color: theme?.dashboard?.status?.correct || '#10b981',
        percentage: communityPercentage,
        message: STRING.community_test_popup_desc_less_100,
        level: 'easy'
      };
    }
  }

  // Navigation handlers
  function handleQuestionReview(type, questions, title) {
    if (questions.length > 0) {
      navigate("/questionReview", {
        state: {
          questions: questions,
          title,
          item: testItem,
          lastReadinessScore,
        },
      });
    }
  }

  function handleAllQuestions() {
    handleQuestionReview(
      'all',
      testItem?.test_questions_meta_data || [],
      'All Questions'
    );
  }

  function handleUnansweredQuestions() {
    const questions = testItem?.test_questions_meta_data?.filter(
      item => item.response === STRING.unanswered_tag
    ) || [];
    handleQuestionReview('unanswered', questions, 'Unanswered');
  }

  function handleCorrectQuestions() {
    const questions = testItem?.test_questions_meta_data?.filter(
      item => item.response === STRING.correct_tag
    ) || [];
    handleQuestionReview('correct', questions, 'Correct');
  }

  function handleIncorrectQuestions() {
    const questions = testItem?.test_questions_meta_data?.filter(
      item => item.response === STRING.incorrect_tag
    ) || [];
    handleQuestionReview('incorrect', questions, 'Incorrect');
  }

  function handleDomainQuestions(domain) {
    const questions = testItem?.test_questions_meta_data?.filter(
      e => e.topic_id === domain.id
    ) || [];
    handleQuestionReview('domain', questions, domain.title);
  }

  // Action handlers
  async function handleDeleteTest() {
    deleteTestHistory(testItem);
    navigate("/history", { state: { isShowReview: false } });
  }

  function handleRetakeTest() {
    const questionData = testItem?.test_questions_meta_data || [];
    const retakeItem = {
      test_name: testItem.test_name,
      test_questions_meta_data: questionData,
      num_questions: questionData.length,
      is_retake_test: true,
      is_show_answer: false,
    };
    navigate("/test", { state: retakeItem });
  }

  const stats = getTestStats();
  const communityStats = getCommunityStats();
  const testName = testItem?.test_name?.trim() || "Test Result";
  const testDate = testItem?.date?.trim() || new Date().toLocaleDateString();

  const calculateStats = () => {
    const statsData = [
      {
        icon: faListAlt,
        stat: stats.totalQuestions,
        label: 'Total Questions',
        color: theme?.primary?.orange?.[500],
        iconType: 'fontawesome',
        ...(stats.totalQuestions > 0 && {
          link: 'Review',
          onLinkClick: handleAllQuestions
        })
      },
      
      {
        icon: faCheckCircle,
        stat: `${stats.correct}`,
        label: 'Total Correct',
        color: theme?.primary?.green?.[500],
        iconType: 'fontawesome',
        ...(stats.correct > 0 && {
          link: 'Review',
          onLinkClick: handleCorrectQuestions
        })
      },
      {
        icon: faTimesCircle,
        stat: `${stats.incorrect}`,
        label: 'Total Incorrect',
        color: theme?.primary?.red?.[500],
        iconType: 'fontawesome',
        ...(stats.incorrect > 0 && {
          link: 'Review',
          onLinkClick: handleIncorrectQuestions
        })
      },
      {
        icon: faQuestionCircle,
        stat: `${stats.unanswered}`,
        label: 'Total Unanswered',
        color: theme?.primary?.yellow?.[500],
        iconType: 'fontawesome',
        ...(stats.unanswered > 0 && {
          link: 'Review',
          onLinkClick: handleUnansweredQuestions
        })
      }
    ];
    return statsData;
  }

  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          {/* <TestReviewHeader
            testName={testName}
            testDate={testDate}
            onBack={() => canGoBack ? navigate(-1) : navigate("/history")}
            onDelete={() => setShowDeleteModal(true)}
            theme={theme}
          /> */}
          <CommonHeader
            title={testName}
            subtitle="Test your knowledge and track your progress"
          showBackButton={true}
            onBack={() => canGoBack ? navigate(-1) : navigate("/history")}
            theme={theme}
          />
          <StatsGrid
            stats={calculateStats()}
            theme={theme}
          />

            <div className="w-3/4 mx-auto mt-8 justify-center">
              <TestReviewScore
                score={stats.score}
                avgTime={stats.avgTime}
                theme={theme}
                showCommunityIcon={isVisibleCommunity}
                onCommunityClick={() => setShowCommunityModal(true)}
              />
            </div>
            {/* Readiness Score Impact */}
            {lastReadinessScoreInt && (
                <div className="w-3/4 mx-auto mt-8 justify-center">
                <ReadinessScoreImpact
                  lastReadinessScoreInt={lastReadinessScoreInt}
                  profileReadinessScoreInt={profileReadinessScoreInt}
                  theme={theme}
                />
                </div>
              )}
            <div className="flex justify-center mt-8">
              <button
                onClick={handleRetakeTest}
                className="px-6 py-3 text-white rounded-lg hover:scale-105 transition-colors duration-200 font-medium"
                style={{
                  background: `linear-gradient(135deg, ${theme?.primary?.orange?.[300] || '#3b82f6'}, ${theme?.primary?.orange?.[500] || '#2563eb'})`,
                  color: 'white',
                }}
              >
                Retake Test
              </button>
            </div>

          <div className="space-y-8 mt-8">
            {/* First Row - Score, Readiness Impact, and Actions */}
            <div className="space-y-6">
              {/* Score Card */}
              

              

              {/* Actions */}
              
            </div>

            {/* Second Row - Questions and Domains */}
            <div >
             
              {/* Domain Scores */}
              <TestReviewDomains
                domains={domainList}
                onDomainClick={handleDomainQuestions}
                theme={theme}
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <DeleteTestModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteTest}
        theme={theme}
      />

      <TestReviewCommunity
        isOpen={showCommunityModal}
        type="Test"
        onClose={() => setShowCommunityModal(false)}
        communityStats={communityStats}
        theme={theme}
      />
    </div>
  );
};

export default TestReview;